/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Support Service - Super Admin Support Tools API
 * ============================================================================
 *
 * Service layer for support tools API calls (user lookup, password reset, etc.).
 *
 * ============================================================================
 */

import { get, post } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';
import { UserLookupResult, SupportActionResponse } from '@/types/superadmin.types';

const BASE_URL = '/superadmin/support';

/**
 * Support service for Super Admin
 */
export const supportService = {
  /**
   * Lookup user by email
   */
  async lookupUser(email: string): Promise<UserLookupResult> {
    const response = await get<ApiResponse<UserLookupResult>>(`${BASE_URL}/users/lookup`, { email });
    return response.data;
  },

  /**
   * Send password reset email to user
   */
  async sendPasswordReset(userId: string): Promise<SupportActionResponse> {
    const response = await post<ApiResponse<SupportActionResponse>>(
      `${BASE_URL}/users/${userId}/reset-password`
    );
    return response.data;
  },

  /**
   * Unlock a locked user account
   */
  async unlockAccount(userId: string): Promise<SupportActionResponse> {
    const response = await post<ApiResponse<SupportActionResponse>>(
      `${BASE_URL}/users/${userId}/unlock`
    );
    return response.data;
  },

  /**
   * Disable MFA for a user
   */
  async disableMfa(userId: string): Promise<SupportActionResponse> {
    const response = await post<ApiResponse<SupportActionResponse>>(
      `${BASE_URL}/users/${userId}/disable-mfa`
    );
    return response.data;
  },

  /**
   * Enforce MFA for a user (user must set up MFA on next login)
   */
  async enforceMfa(userId: string): Promise<SupportActionResponse> {
    const response = await post<ApiResponse<SupportActionResponse>>(
      `${BASE_URL}/users/${userId}/enforce-mfa`
    );
    return response.data;
  },

  /**
   * Remove MFA enforcement for a user
   */
  async removeMfaEnforcement(userId: string): Promise<SupportActionResponse> {
    const response = await post<ApiResponse<SupportActionResponse>>(
      `${BASE_URL}/users/${userId}/remove-mfa-enforcement`
    );
    return response.data;
  },

  /**
   * Suspend a user account
   */
  async suspendAccount(userId: string, reason?: string): Promise<SupportActionResponse> {
    const response = await post<ApiResponse<SupportActionResponse>>(
      `${BASE_URL}/users/${userId}/suspend`,
      reason ? { reason } : undefined
    );
    return response.data;
  },

  /**
   * Activate a user account
   */
  async activateAccount(userId: string): Promise<SupportActionResponse> {
    const response = await post<ApiResponse<SupportActionResponse>>(
      `${BASE_URL}/users/${userId}/activate`
    );
    return response.data;
  },

  /**
   * Deactivate a user account (permanent)
   */
  async deactivateAccount(userId: string, reason?: string): Promise<SupportActionResponse> {
    const response = await post<ApiResponse<SupportActionResponse>>(
      `${BASE_URL}/users/${userId}/deactivate`,
      reason ? { reason } : undefined
    );
    return response.data;
  },
};

export default supportService;
